import {useEffect, useId, useLayoutEffect, useRef, useState} from 'react';
import type {KeyboardEvent, PointerEvent, ReactNode} from 'react';
import {readStored, saveStored} from '../state';
import {clampNavigationWidth, DEFAULT_NAVIGATION_WIDTH, navigationWidthBounds, normalizeNavigationWidth} from '../reader-navigation';
import '../reader-navigation.css';

/** Owns only navigation geometry; changing width never remounts the PDF surface. */
export function ReaderNavigation({storageKey, label, children}: {storageKey: string; label: string; children: ReactNode}) {
  const panel = useRef<HTMLDivElement>(null);
  const handle = useRef<HTMLDivElement>(null);
  const id = useId();
  const [preferredWidth, setPreferredWidth] = useState(() => normalizeNavigationWidth(readStored<unknown>(storageKey, DEFAULT_NAVIGATION_WIDTH)));
  const [containerWidth, setContainerWidth] = useState<number>();
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{pointerId: number; x: number; width: number; previous: number; latest: number; ownsCursor: boolean} | null>(null);
  const bounds = containerWidth === undefined ? {min: 160, max: Number.POSITIVE_INFINITY} : navigationWidthBounds(containerWidth);
  const width = containerWidth === undefined ? preferredWidth : clampNavigationWidth(preferredWidth, containerWidth);

  useLayoutEffect(() => {
    const container = panel.current?.parentElement;
    if (!container) return;
    const measure = () => {
      const next = container.getBoundingClientRect().width;
      // A hidden reader must not overwrite the last visible constraints/preference.
      if (next > 0) setContainerWidth(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  function finish(commit: boolean) {
    const active = drag.current;
    if (!active) return;
    drag.current = null;
    if (handle.current?.hasPointerCapture(active.pointerId)) handle.current.releasePointerCapture(active.pointerId);
    if (active.ownsCursor) document.body.classList.remove('is-resizing');
    setDragging(false);
    if (commit) saveStored(storageKey, active.latest);
    else setPreferredWidth(active.previous);
  }

  useEffect(() => {
    const cancel = () => finish(false);
    window.addEventListener('blur', cancel);
    return () => {
      window.removeEventListener('blur', cancel);
      const active = drag.current;
      drag.current = null;
      if (active?.ownsCursor) document.body.classList.remove('is-resizing');
    };
  }, []);

  function constrain(value: number) {
    const available = panel.current?.parentElement?.getBoundingClientRect().width;
    return available ? clampNavigationWidth(value, available) : width;
  }

  function start(event: PointerEvent<HTMLDivElement>) {
    if (!event.isPrimary || event.button !== 0 || drag.current) return;
    event.preventDefault();
    const actual = panel.current?.getBoundingClientRect().width ?? width;
    drag.current = {pointerId: event.pointerId, x: event.clientX, width: actual, previous: preferredWidth, latest: actual, ownsCursor: !document.body.classList.contains('is-resizing')};
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.focus({preventScroll: true});
    document.body.classList.add('is-resizing');
    setDragging(true);
  }

  function move(event: PointerEvent<HTMLDivElement>) {
    const active = drag.current;
    if (!active || active.pointerId !== event.pointerId) return;
    active.latest = constrain(active.width + event.clientX - active.x);
    setPreferredWidth(active.latest);
  }

  function keyResize(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' && drag.current) {event.preventDefault(); finish(false); return;}
    const step = event.shiftKey ? 40 : 16;
    const next = event.key === 'ArrowLeft' ? width - step : event.key === 'ArrowRight' ? width + step : event.key === 'Home' ? bounds.min : event.key === 'End' && Number.isFinite(bounds.max) ? bounds.max : undefined;
    if (next === undefined || drag.current) return;
    event.preventDefault();
    const constrained = constrain(next);
    setPreferredWidth(constrained);
    saveStored(storageKey, constrained);
  }

  return <div ref={panel} className="reader-navigation-panel" style={{width}}>
    <aside id={id} className="reader-navigation" aria-label={label}>{children}</aside>
    <div ref={handle} className={`reader-navigation-resizer${dragging ? ' dragging' : ''}`} role="separator" tabIndex={0}
      aria-label={`调整${label}宽度`} aria-controls={id} aria-orientation="vertical"
      aria-valuemin={Math.round(bounds.min)} aria-valuemax={Number.isFinite(bounds.max) ? Math.round(bounds.max) : undefined} aria-valuenow={Math.round(width)} aria-valuetext={`${Math.round(width)} 像素`}
      onPointerDown={start} onPointerMove={move}
      onPointerUp={event => {if (event.pointerId === drag.current?.pointerId) finish(true);}}
      onPointerCancel={event => {if (event.pointerId === drag.current?.pointerId) finish(false);}}
      onLostPointerCapture={event => {if (event.pointerId === drag.current?.pointerId) finish(false);}}
      onKeyDown={keyResize}/>
  </div>;
}
